import { RotateCw } from "lucide-react";
import Button from "./Button";
import cn from "./cn";

/**
 * EmptyState — one line on what will appear and how, and at most one action
 * the user can take. No illustration tile (DESIGN.md › Page Patterns › Empty).
 */
export function EmptyState({ title, description, action, icon: Icon, className }) {
  return (
    <div className={cn("flex flex-col items-center px-4 py-10 text-center", className)}>
      {Icon ? <Icon aria-hidden="true" className="mb-3 h-5 w-5 text-tertiary" /> : null}
      <p className="text-panel-title text-foreground">{title}</p>
      {description ? <p className="mt-1 max-w-md text-body-sm text-secondary">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/**
 * ErrorState — what failed in plain words and a way to try again. Raw status
 * codes and server messages stay out of it; pass a plain `description`.
 */
export function ErrorState({
  title = "This couldn't be loaded",
  description = "Check your connection and try again.",
  onRetry,
  retryLabel = "Try again",
  className,
}) {
  return (
    <div role="alert" className={cn("flex flex-col items-center px-4 py-10 text-center", className)}>
      <p className="text-panel-title text-foreground">{title}</p>
      {description ? <p className="mt-1 max-w-md text-body-sm text-secondary">{description}</p> : null}
      {onRetry ? (
        <Button className="mt-4" size="sm" icon={RotateCw} onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

const WIDTHS = ["w-2/5", "w-3/5", "w-1/4", "w-1/2", "w-1/3"];

/** SkeletonRows — placeholder rows for an operational list or table while it loads. */
export function SkeletonRows({ rows = 5, columns = 4, label = "Loading", className }) {
  return (
    <div role="status" aria-label={label} className={cn("divide-y divide-subtle", className)}>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex h-row items-center gap-6 px-4">
          {Array.from({ length: columns }, (__, col) => (
            <span
              key={col}
              aria-hidden="true"
              className={cn(
                "h-3 rounded-full bg-surface-sunken motion-safe:animate-pulse",
                col === 0 ? "flex-[2]" : "flex-1",
                WIDTHS[(row + col) % WIDTHS.length]
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
